import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import FormField from '../../../components/Auth/FormField';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Validate token on component mount
    const validateToken = async () => {
      try {
        // Simulate token validation API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Simulate token validation (90% success rate)
        const isValid = Math.random() > 0.1;
        setTokenValid(isValid);
      } catch (error) {
        setTokenValid(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setTokenValid(false);
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 11) {
      newErrors.password = 'Password must be at least 11 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate password reset API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success
      alert('Admin password reset successfully! You can now log in with your new password.');
      navigate('/admin/auth/login');
    } catch (error) {
      setErrors({ submit: 'Failed to reset password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: 'bg-jet-red-500' };
      case 2:
        return { label: 'Weak', color: 'bg-red-400' };
      case 3:
        return { label: 'Fair', color: 'bg-yellow-400' };
      case 4:
        return { label: 'Good', color: 'bg-jet-green-400' };
      case 5:
        return { label: 'Strong', color: 'bg-jet-green-500' };
      default:
        return { label: '', color: '' };
    }
  };

  if (tokenValid === null) {
    return (
      <AuthLayout
        title="Validating Reset Link"
        subtitle="Please wait while we validate your admin password reset link"
        icon={
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
        }
        iconBgColor="bg-jet-blue-100"
        iconColor="text-jet-blue-600"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-red-600 mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Validating admin reset link...</p>
        </div>
      </AuthLayout>
    );
  }

  if (tokenValid === false) {
    return (
      <AuthLayout
        title="Invalid Reset Link"
        subtitle="This admin password reset link is invalid or has expired"
        icon={
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        }
        iconBgColor="bg-jet-red-100"
        iconColor="text-jet-red-600"
      >
        <div className="text-center space-y-6">
          <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-4">
            <p className="text-sm text-jet-red-800">
              This admin password reset link is invalid or has expired. 
              Please request a new one to reset your admin password.
            </p>
          </div>
          
          <div className="space-y-4">
            <LoadingButton
              onClick={() => navigate('/admin/auth/forgot-password')}
              variant="primary"
              className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
            >
              Request New Reset Link
            </LoadingButton>
            
            <button
              onClick={() => navigate('/admin/auth/login')}
              className="w-full border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium py-3 px-4 rounded-md transition-colors"
            >
              Back to Admin Login
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthInfo = getPasswordStrengthLabel(passwordStrength);

  const lockIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
  );

  return (
    <AuthLayout
      title="Reset Admin Password"
      subtitle="Enter your new admin password below to complete the reset process"
      icon={lockIcon}
      iconBgColor="bg-jet-red-100"
      iconColor="text-jet-red-600"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Security Notice */}
        <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-red-900 mb-1">Enhanced Security Required</h4>
              <p className="text-sm text-jet-red-800">
                Admin passwords must meet enhanced security requirements for system protection.
              </p>
            </div>
          </div>
        </div>

        {/* New Password */}
        <FormField
          label="New Admin Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Enter new admin password"
          required
          error={errors.password}
          showPasswordToggle
          showPassword={showPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
        >
          {/* Password Strength Indicator */}
          {formData.password && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-jet-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${strengthInfo.color}`}
                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-jet-gray-600">{strengthInfo.label}</span>
              </div>
            </div>
          )}
        </FormField>

        {/* Confirm Password */}
        <FormField
          label="Confirm New Password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm new admin password"
          required
          error={errors.confirmPassword}
          showPasswordToggle
          showPassword={showConfirmPassword}
          onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
        />

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-3">
            <p className="text-sm text-jet-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Admin Password Requirements */}
        <div className="bg-jet-blue-50 border border-jet-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-jet-blue-900 mb-2">Admin Password Requirements:</h4>
          <ul className="text-sm text-jet-blue-800 space-y-1">
            <li className="flex items-center">
              <span className={`mr-2 ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                {formData.password.length >= 11 ? '✓' : '○'}
              </span>
              At least 11 characters long
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                {/[a-z]/.test(formData.password) ? '✓' : '○'}
              </span>
              Contains lowercase letter
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                {/[A-Z]/.test(formData.password) ? '✓' : '○'}
              </span>
              Contains uppercase letter
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/\d/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                {/\d/.test(formData.password) ? '✓' : '○'}
              </span>
              Contains number
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${/[@$!%*?&]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                {/[@$!%*?&]/.test(formData.password) ? '✓' : '○'}
              </span>
              Contains special character (@$!%*?&)
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Resetting Admin Password..."
          variant="primary"
          className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
        >
          Reset Admin Password
        </LoadingButton>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/admin/auth/login')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            Back to Admin Login
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AdminResetPasswordPage;